import {
  DATABRICKS_AUTHORING_CERTIFICATION_CHECKS,
  DATABRICKS_RELEASE_CERTIFICATION_CHECKS,
  type DatabricksCertificationBlockingTier,
  databricksCertificationTier,
} from "./certification.js";

export interface DatabricksCertificationEnvironmentIssue {
  checkId: string;
  variables: string[];
  message: string;
}

type Environment = Readonly<Record<string, string | undefined>>;

const REQUIREMENTS: Readonly<
  Record<string, { all?: readonly string[]; any?: ReadonlyArray<readonly string[]> }>
> = {
  "ai-gateway": { all: ["DATABRICKS_MODEL"] },
  sql: { all: ["DATABRICKS_WAREHOUSE_ID"] },
  "unity-catalog": { all: ["DATABRICKS_CATALOG", "DATABRICKS_SCHEMA"] },
  "agent-services": {
    all: [
      "DATABRICKS_AGENT_SERVICES_TEST",
      "DATABRICKS_AGENT_SERVICE_CONNECTION",
      "DATABRICKS_CATALOG",
      "DATABRICKS_SCHEMA",
    ],
  },
  "uc-denial": { all: ["DATABRICKS_WAREHOUSE_ID", "DATABRICKS_DENIED_TABLE"] },
  "uc-allowed-select": { all: ["DATABRICKS_WAREHOUSE_ID", "DATABRICKS_ALLOWED_TABLE"] },
  "catalog-preflight-denial": { all: ["DATABRICKS_CATALOG"] },
  "mutation-approval": { all: ["DATABRICKS_PIPELINE_ID"] },
  "model-serving": { all: ["DATABRICKS_MODEL"] },
  rag: {
    all: ["DATABRICKS_MODEL"],
    any: [
      ["DATABRICKS_AI_SEARCH_INDEX", "DATABRICKS_VECTOR_INDEX"],
      ["DATABRICKS_AI_SEARCH_TEXT_COLUMN", "DATABRICKS_VECTOR_TEXT_COLUMN"],
    ],
  },
  "rag-evaluation": { all: ["DATABRICKS_RAG_EVAL_JOB_ID"] },
  "responses-agent": { all: ["DATABRICKS_RESPONSES_AGENT_ENDPOINT"] },
  "ai-search": {
    any: [
      ["DATABRICKS_AI_SEARCH_INDEX", "DATABRICKS_VECTOR_INDEX"],
      ["DATABRICKS_AI_SEARCH_TEXT_COLUMN", "DATABRICKS_VECTOR_TEXT_COLUMN"],
    ],
  },
  genie: { all: ["DATABRICKS_GENIE_SPACE_ID"] },
  "managed-mcp": {
    all: ["DATABRICKS_OBO_TOKEN", "DATABRICKS_MANAGED_MCP_URL", "DATABRICKS_MANAGED_MCP_TOOL"],
  },
  "genie-agent-mode": { all: ["DATABRICKS_GENIE_AGENT_MODE_ID"] },
  "feature-serving": {
    all: ["DATABRICKS_FEATURE_ENDPOINT", "DATABRICKS_FEATURE_RECORDS_JSON"],
  },
  lakeflow: { all: ["DATABRICKS_PIPELINE_ID"] },
  jobs: { all: ["DATABRICKS_JOB_ID"] },
  notebook: { all: ["DATABRICKS_NOTEBOOK_PATH"] },
  volumes: { all: ["DATABRICKS_CATALOG", "DATABRICKS_SCHEMA", "DATABRICKS_VOLUME"] },
  lakebase: { all: ["ENDPOINT_NAME", "PGHOST", "PGDATABASE", "PGUSER"] },
  "lakebase-app-restart": { all: ["FABRIC_DATABRICKS_APP_RECOVERY_EVIDENCE_PATH"] },
  "dynamic-agent": {
    all: [
      "FABRIC_DATABRICKS_DYNAMIC_EVIDENCE_PATH",
      "FABRIC_DATABRICKS_DYNAMIC_IMPLEMENTATION_MODEL",
      "DATABRICKS_JOB_ID",
    ],
  },
  lineage: {
    all: [
      "ENDPOINT_NAME",
      "PGHOST",
      "PGDATABASE",
      "PGUSER",
      "DATABRICKS_CATALOG",
      "DATABRICKS_ALLOWED_TABLE",
    ],
  },
  obo: { all: ["DATABRICKS_OBO_TOKEN", "DATABRICKS_OBO_DIFFERENTIAL_TABLE"] },
  "system-tables": { all: ["DATABRICKS_WAREHOUSE_ID", "DATABRICKS_SYSTEM_TABLES_TEST"] },
  "actual-cost-reconciliation": {
    all: [
      "DATABRICKS_WAREHOUSE_ID",
      "DATABRICKS_SYSTEM_TABLES_TEST",
      "DATABRICKS_COST_SCOPE_KEY",
      "DATABRICKS_ESTIMATED_COST_USD",
    ],
  },
  "databricks-app": { all: ["DATABRICKS_APP_URL"] },
  "approval-provenance": { all: ["FABRIC_DATABRICKS_AUTHORING_TEST"] },
  "jobs-authoring": {
    all: ["DATABRICKS_AUTHORING_JOB_SPEC", "DATABRICKS_AUTHORING_COMPUTE_POLICY"],
  },
  "lakeflow-authoring": { all: ["DATABRICKS_AUTHORING_LAKEFLOW_SPEC"] },
  "ai-search-admin": { all: ["DATABRICKS_AUTHORING_AI_SEARCH_SPEC"] },
  "serving-admin": { all: ["DATABRICKS_AUTHORING_SERVING_SPEC"] },
  "uc-admin-obo-execution": {
    all: [
      "DATABRICKS_OBO_TOKEN",
      "DATABRICKS_AUTHORING_EXECUTING_PRINCIPAL_ID",
      "DATABRICKS_AUTHORING_EXECUTING_UC_PRINCIPAL",
      "DATABRICKS_AUTHORING_UC_CATALOG",
      "DATABRICKS_AUTHORING_UC_SCHEMA_PREFIX",
    ],
  },
  "workspace-write": { all: ["DATABRICKS_AUTHORING_WORKSPACE_ROOT"] },
  "secrets-write": {
    all: ["DATABRICKS_AUTHORING_SECRET_SCOPE_PREFIX", "DATABRICKS_AUTHORING_SECRET_VALUE"],
  },
  "genie-authoring": { all: ["DATABRICKS_AUTHORING_GENIE_SPEC"] },
  "jobs-classic-authoring": {
    all: ["DATABRICKS_AUTHORING_CLASSIC_JOB_SPEC", "DATABRICKS_AUTHORING_COMPUTE_POLICY"],
  },
  "ai-search-delta-sync-admin": { all: ["DATABRICKS_AUTHORING_DELTA_SYNC_SPEC"] },
  "serving-provisioned-throughput-admin": {
    all: ["DATABRICKS_AUTHORING_PROVISIONED_THROUGHPUT_SPEC"],
  },
};

/** Resolve blocking-tier checks plus explicit additions. Explicit ids never remove tier defaults. */
export function resolveDatabricksRequiredChecks(
  requiredTiers: readonly DatabricksCertificationBlockingTier[],
  explicitlyRequired: readonly string[] = [],
): string[] {
  const builtIns = [
    ...DATABRICKS_RELEASE_CERTIFICATION_CHECKS,
    ...DATABRICKS_AUTHORING_CERTIFICATION_CHECKS,
  ];
  return [
    ...new Set([
      ...builtIns.filter((id) =>
        requiredTiers.some((tier) => tier === databricksCertificationTier(id)),
      ),
      ...explicitlyRequired,
    ]),
  ];
}

/**
 * Return actionable, secret-free preflight failures for a protected certification environment.
 * The certification runner remains authoritative for runtime configuration and service behavior.
 */
export function validateDatabricksCertificationEnvironment(
  environment: Environment,
  requiredChecks: readonly string[],
): DatabricksCertificationEnvironmentIssue[] {
  const issues: DatabricksCertificationEnvironmentIssue[] = [];
  if (!present(environment, "DATABRICKS_HOST")) {
    issues.push(issue("identity", ["DATABRICKS_HOST"], "workspace host is missing"));
  }
  const hasPat = present(environment, "DATABRICKS_TOKEN");
  const hasM2m =
    present(environment, "DATABRICKS_CLIENT_ID") &&
    present(environment, "DATABRICKS_CLIENT_SECRET");
  if (!hasPat && !hasM2m) {
    issues.push(
      issue(
        "identity",
        ["DATABRICKS_TOKEN", "DATABRICKS_CLIENT_ID", "DATABRICKS_CLIENT_SECRET"],
        "configure a PAT or both OAuth M2M credentials",
      ),
    );
  }
  if (environment.FABRIC_DATABRICKS_REQUIRE_USER_ISOLATION === "1") {
    const missing = ["DATABRICKS_OBO_TOKEN_A", "DATABRICKS_OBO_TOKEN_B"].filter(
      (name) => !present(environment, name),
    );
    if (missing.length > 0) {
      issues.push(
        issue(
          "app-user-isolation",
          missing,
          `two distinct OBO identities are required; missing ${missing.join(", ")}`,
        ),
      );
    }
  }
  if (requiredChecks.some((checkId) => databricksCertificationTier(checkId) === "A")) {
    const missing = [
      "FABRIC_DATABRICKS_AUTHORING_TEST",
      "DATABRICKS_OBO_TOKEN",
      "DATABRICKS_AUTHORING_EXECUTING_PRINCIPAL_ID",
      "DATABRICKS_AUTHORING_STEWARD_ID",
    ].filter((name) => !present(environment, name));
    if (missing.length > 0) {
      issues.push(issue("tier-a", missing, `missing ${missing.join(", ")}`));
    }
  }

  for (const checkId of new Set(requiredChecks)) {
    const requirement = REQUIREMENTS[checkId];
    if (!requirement) continue;
    const missing = (requirement.all ?? []).filter((name) => !present(environment, name));
    const missingAlternatives = (requirement.any ?? []).filter(
      (names) => !names.some((name) => present(environment, name)),
    );
    for (const names of missingAlternatives) missing.push(names.join(" or "));
    if (missing.length > 0) {
      issues.push(issue(checkId, missing, `missing ${missing.join(", ")}`));
    }
  }

  if (
    requiredChecks.includes("ai-gateway") &&
    present(environment, "DATABRICKS_MODEL") &&
    !environment.DATABRICKS_MODEL?.startsWith("system.ai.")
  ) {
    issues.push(
      issue(
        "ai-gateway",
        ["DATABRICKS_MODEL"],
        "DATABRICKS_MODEL must name a system.ai model service",
      ),
    );
  }
  for (const checkId of ["system-tables", "actual-cost-reconciliation"]) {
    if (
      requiredChecks.includes(checkId) &&
      present(environment, "DATABRICKS_SYSTEM_TABLES_TEST") &&
      environment.DATABRICKS_SYSTEM_TABLES_TEST !== "1"
    ) {
      issues.push(
        issue(
          checkId,
          ["DATABRICKS_SYSTEM_TABLES_TEST"],
          "DATABRICKS_SYSTEM_TABLES_TEST must be 1",
        ),
      );
    }
  }
  if (
    requiredChecks.includes("agent-services") &&
    present(environment, "DATABRICKS_AGENT_SERVICES_TEST") &&
    environment.DATABRICKS_AGENT_SERVICES_TEST !== "1"
  ) {
    issues.push(
      issue(
        "agent-services",
        ["DATABRICKS_AGENT_SERVICES_TEST"],
        "DATABRICKS_AGENT_SERVICES_TEST must be 1",
      ),
    );
  }
  return issues;
}

function present(environment: Environment, name: string): boolean {
  return Boolean(environment[name]?.trim());
}

function issue(
  checkId: string,
  variables: string[],
  message: string,
): DatabricksCertificationEnvironmentIssue {
  return { checkId, variables, message };
}
